/**
 * Impulse Response / Step Response — reconstructed client-side from the
 * complex frequency response the backend already returns (mag + phase, see
 * Phase/Group Delay charts), no new backend computation. REW/EASE staples
 * this app didn't have at all before.
 *
 * Method: resample the measured (log-spaced, band-limited 20-20000 Hz)
 * complex response onto a uniform linear frequency grid, build a
 * Hermitian-symmetric full spectrum (zero outside the measured band — we
 * have no data there, and zero is the more honest choice than inventing a
 * flat extrapolation), inverse-FFT it, and read off the real part as a
 * time-domain impulse response. Step response is its running (cumulative)
 * sum.
 *
 * This is a genuinely band-limited reconstruction, not a full-bandwidth
 * measurement — expect some ringing near the edges of the measured band
 * (20 Hz / 20 kHz), same as any windowed-spectrum IR. Amplitude is
 * peak-normalised (relative), not calibrated SPL, matching every other
 * chart in this dock that derives from the same relative magnitude data.
 */
import { fft, ifft, isPowerOfTwo, nextPowerOfTwo } from './fft';
import { unwrapPhase } from './phase';
import { magToDb } from './series';
import { inflatePadForYAxis } from './axisMargin';
import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLinearScale, linearToPlot, type LinearScale } from './scales';
import { linearTicks } from './ticks';
import { defaultMeasureWidth, type YTick } from './geometry';

const SPL_P_REF = 2e-5;

function dbToMag(db: number): number {
  return SPL_P_REF * 10 ** (db / 20);
}

/** Two-pointer merge: interpolates (dB-magnitude, unwrapped-phase) from the
 *  sorted `freqs` grid onto the sorted `targetFreqs` grid. Points outside
 *  [freqs[0], freqs[last]] are left at (0, 0) — i.e. silent, see module
 *  header. Both inputs must be ascending. */
function interpolateComplexOntoLinearGrid(
  freqs: number[],
  db: number[],
  phaseUnwrapped: number[],
  targetFreqs: number[],
): { re: Float64Array; im: Float64Array } {
  const n = targetFreqs.length;
  const re = new Float64Array(n);
  const im = new Float64Array(n);
  if (freqs.length < 2) return { re, im };
  const fMin = freqs[0]!;
  const fMax = freqs[freqs.length - 1]!;

  let j = 0;
  for (let i = 0; i < n; i++) {
    const f = targetFreqs[i]!;
    if (f < fMin || f > fMax) continue; // stays silent (0, 0)
    while (j < freqs.length - 2 && freqs[j + 1]! < f) j++;
    const f0 = freqs[j]!;
    const f1 = freqs[j + 1]!;
    const t = f1 > f0 ? (f - f0) / (f1 - f0) : 0;
    const dbInterp = db[j]! + t * (db[j + 1]! - db[j]!);
    const phInterp = phaseUnwrapped[j]! + t * (phaseUnwrapped[j + 1]! - phaseUnwrapped[j]!);
    if (!Number.isFinite(dbInterp)) continue; // e.g. an HPF-floored (NaN-dB) bin — leave silent
    const magInterp = dbToMag(dbInterp);
    re[i] = magInterp * Math.cos(phInterp);
    im[i] = magInterp * Math.sin(phInterp);
  }
  return { re, im };
}

export interface ImpulseResponseSample {
  timeMs: number;
  /** Peak-normalised to [-1, 1] (impulse) or its own peak-abs (step) —
   *  relative, not calibrated SPL. */
  value: number;
}

export interface ImpulseResponseOptions {
  /** Synthetic sample rate for the reconstruction, Hz. Higher = finer time
   *  resolution, more FFT work. Default 48000. */
  sampleRateHz?: number;
  /** FFT size — rounded up to the next power of 2 if it isn't one. Larger =
   *  finer frequency-domain resolution (less spectral leakage from the
   *  interpolation) at the cost of more compute. Default 8192
   *  (~171 ms total buffer at 48 kHz, comfortably covering the displayed
   *  window with headroom before wraparound). */
  fftSize?: number;
  /** Displayed time window, ms, positive side. Default 50. */
  maxTimeMs?: number;
  /** Displayed time window, ms, negative side (small "pre-ring" guard so a
   *  peak landing just before t=0 isn't clipped). Default 2. */
  guardMs?: number;
}

/** Defaults the drawers use when no `options` are passed — exported so a
  *  chart shell can align its hover x-window to the drawn curve exactly. */
export const DEFAULT_IMPULSE_OPTIONS: Required<ImpulseResponseOptions> = {
  sampleRateHz: 48000,
  fftSize: 8192,
  maxTimeMs: 50,
  guardMs: 2,
};


/** Reconstructs a band-limited impulse response from (freqs, mag, phase).
 *  Returns null if there isn't enough data (fewer than 2 points, or
 *  mismatched array lengths) to interpolate from. Samples are sorted by
 *  time and peak-normalised to [-1, 1]. */
export function computeImpulseResponseSamples(
  freqs: number[],
  mag: number[],
  phase: number[],
  opts?: ImpulseResponseOptions,
): ImpulseResponseSample[] | null {
  const n = Math.min(freqs.length, mag.length, phase.length);
  if (n < 2) return null;

  const sampleRateHz = opts?.sampleRateHz ?? 48000;
  const fftSize = isPowerOfTwo(opts?.fftSize ?? 8192) ? (opts?.fftSize ?? 8192) : nextPowerOfTwo(opts?.fftSize ?? 8192);
  const maxTimeMs = opts?.maxTimeMs ?? 50;
  const guardMs = opts?.guardMs ?? 2;

  const freqsTrim = freqs.slice(0, n);
  const dbTrim = magToDb(mag.slice(0, n));
  const phaseTrim = unwrapPhase(phase.slice(0, n));

  const nBins = fftSize / 2 + 1;
  const binHz = sampleRateHz / fftSize;
  const linearFreqs = Array.from({ length: nBins }, (_, i) => i * binHz);

  const { re: posRe, im: posIm } = interpolateComplexOntoLinearGrid(freqsTrim, dbTrim, phaseTrim, linearFreqs);
  posIm[0] = 0; // DC bin must be real for a real-valued IFFT output
  posIm[nBins - 1] = 0; // Nyquist bin must be real, same reason

  const re = new Float64Array(fftSize);
  const im = new Float64Array(fftSize);
  for (let k = 0; k < nBins; k++) {
    re[k] = posRe[k]!;
    im[k] = posIm[k]!;
  }
  // Hermitian mirror: X[N-k] = conj(X[k]) for k = 1 .. N/2-1, so the IFFT's
  // imaginary part cancels to ~0 and the real part is a valid real signal.
  for (let k = 1; k < fftSize / 2; k++) {
    re[fftSize - k] = posRe[k]!;
    im[fftSize - k] = -posIm[k]!;
  }

  ifft(re, im);

  // Circular-buffer time convention: the first half of the IFFT output is
  // "positive time" (0 .. bufferMs/2), the second half aliases to negative
  // time (-bufferMs/2 .. 0) — standard for reading a periodic IFFT result
  // as a causal-ish signal centred near n=0.
  const halfN = fftSize / 2;
  const raw: ImpulseResponseSample[] = [];
  for (let idx = 0; idx < fftSize; idx++) {
    const nSigned = idx < halfN ? idx : idx - fftSize;
    const timeMs = (nSigned / sampleRateHz) * 1000;
    if (timeMs < -guardMs || timeMs > maxTimeMs) continue;
    raw.push({ timeMs, value: re[idx]! });
  }
  raw.sort((a, b) => a.timeMs - b.timeMs);

  const peak = raw.reduce((m, s) => Math.max(m, Math.abs(s.value)), 0) || 1;
  return raw.map((s) => ({ timeMs: s.timeMs, value: s.value / peak }));
}

/** Running sum of an impulse response — the step response, re-normalised to
 *  its own peak-abs value so it stays comparably scaled on the same [-1, 1]
 *  axis as the impulse response it derives from. */
export function computeStepResponseSamples(impulse: ImpulseResponseSample[]): ImpulseResponseSample[] {
  let acc = 0;
  const raw = impulse.map((s) => {
    acc += s.value;
    return { timeMs: s.timeMs, value: acc };
  });
  const peak = raw.reduce((m, s) => Math.max(m, Math.abs(s.value)), 0) || 1;
  return raw.map((s) => ({ timeMs: s.timeMs, value: s.value / peak }));
}

// ── Geometry (paint-layer-agnostic frame/scale/points, mirrors echogram.ts) ─

export interface ImpulseResponsePoint {
  timeMs: number;
  value: number;
  x: number;
  y: number;
}

export interface ImpulseResponseInput {
  freqs: number[];
  mag: number[];
  phase: number[];
  width: number;
  height: number;
  pad?: ChartPad;
  mode?: 'impulse' | 'step';
  options?: ImpulseResponseOptions;
  measureWidth?: (label: string) => number;
}

export interface ImpulseResponseGeometry {
  empty: boolean;
  /** Effective pad after y-axis label collision avoidance. */
  pad: ChartPad;
  frame: PlotFrame;
  mode: 'impulse' | 'step';
  maxTimeMs: number;
  guardMs: number;
  xScale: LinearScale;
  yScale: LinearScale;
  points: ImpulseResponsePoint[];
  xTicks: { value: number; x: number; label: string }[];
  yTicks: YTick[];
}

export function computeImpulseResponseGeometry(opts: ImpulseResponseInput): ImpulseResponseGeometry {
  const basePad = opts.pad ?? DEFAULT_CHART_PAD;
  const mode = opts.mode ?? 'impulse';
  const maxTimeMs = opts.options?.maxTimeMs ?? 50;
  const guardMs = opts.options?.guardMs ?? 2;
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;

  const yTickLabels = linearTicks(-1, 1, 0.5).map((v) => v.toFixed(1));
  const pad = inflatePadForYAxis(basePad, yTickLabels, measureWidth);

  const frame = createPlotFrame(opts.width, opts.height, pad);
  const xScale = createLinearScale(frame.plotW, -guardMs, maxTimeMs, false);
  const yScale = createLinearScale(frame.plotH, -1, 1, true);

  const empty = {
    empty: true, pad, frame, mode, maxTimeMs, guardMs, xScale, yScale,
    points: [], xTicks: [], yTicks: [],
  };
  if (frame.plotW <= 0) return empty;

  const impulse = computeImpulseResponseSamples(opts.freqs, opts.mag, opts.phase, opts.options);
  if (!impulse || !impulse.length) return empty;
  const samples = mode === 'step' ? computeStepResponseSamples(impulse) : impulse;

  const points: ImpulseResponsePoint[] = samples.map((s) => ({
    timeMs: s.timeMs,
    value: s.value,
    x: linearToPlot(xScale, s.timeMs),
    y: linearToPlot(yScale, s.value),
  }));

  const xStep = maxTimeMs > 30 ? 10 : 5;
  const xTicks = linearTicks(-guardMs, maxTimeMs, xStep).map((v) => ({
    value: v, x: linearToPlot(xScale, v), label: `${Math.round(v)}`,
  }));
  const yTicks: YTick[] = linearTicks(-1, 1, 0.5).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: v === 0, label: v.toFixed(1),
  }));

  return { empty: false, pad, frame, mode, maxTimeMs, guardMs, xScale, yScale, points, xTicks, yTicks };
}
